import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Lightbulb, BookOpen, Check, X, Trophy, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RichText, isRichHtml } from "@/components/RichText";
import { Input } from "@/components/ui/input";

export default function LessonPage() {
  const navigate = useNavigate();
  const { courseId, lessonId } = useParams();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [lesson, setLesson] = useState(null);
  const [checks, setChecks] = useState({}); // practice self-check {idx: 'correct'|'wrong'}
  const [pInputs, setPInputs] = useState({});
  const [phase, setPhase] = useState("learn"); // learn | mastery | result
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/courses/lesson/${lessonId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLesson(r.data)).catch((e) => { console.error("lesson load failed", e?.response?.status); toast.error("Couldn't load lesson"); });
  }, [lessonId, token]);

  const norm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, "");
  const checkPractice = (i, ans) => {
    const ok = norm(pInputs[i]) === norm(ans) || (!isNaN(parseFloat(pInputs[i])) && parseFloat(pInputs[i]) === parseFloat(ans));
    setChecks((c) => ({ ...c, [i]: ok ? "correct" : "wrong" }));
  };

  const startMastery = async () => {
    try {
      const r = await axios.post(`${API}/courses/lesson/${lessonId}/mastery/start`, {}, { headers });
      setQuiz(r.data); setAnswers({}); setPhase("mastery");
    } catch (e) { toast.error("Couldn't start mastery check"); }
  };

  const submitMastery = async () => {
    try {
      const r = await axios.post(`${API}/courses/lesson/${lessonId}/mastery/submit`,
        { session_id: quiz.session_id, answers }, { headers });
      setResult(r.data); setPhase("result");
      if (r.data.passed) toast.success(`Mastered! +${r.data.xp} XP`);
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  if (!lesson) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="bg-white border-b px-4 py-3">
        <button onClick={() => navigate("/courses")} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-semibold" data-testid="lesson-back">
          <ArrowLeft className="w-4 h-4" /> Courses
        </button>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-6" data-testid="lesson-page">
        <h1 className="text-2xl font-extrabold text-slate-800 font-['Fredoka'] mb-4">{lesson.title}</h1>

        {phase === "learn" && (
          <>
            <Section icon={BookOpen} title="Concept" color="#6366F1">
              <RichText html={lesson.concept || "Your teacher will add this concept soon. For now, try the practice below!"} className="text-slate-700 leading-relaxed" data-testid="lesson-concept" />
            </Section>
            {lesson.example && (
              <Section icon={Lightbulb} title="Worked Example" color="#F59E0B">
                <RichText html={lesson.example} className={`text-slate-700 ${isRichHtml(lesson.example) ? "" : "font-mono"}`} data-testid="lesson-example" />
              </Section>
            )}
            {lesson.link && (
              <Button variant="outline" className="w-full rounded-xl mb-4" onClick={() => navigate(lesson.link)} data-testid="lesson-trainer-link">
                <ExternalLink className="w-4 h-4 mr-2" /> Practice this on the trainer
              </Button>
            )}

            <Section icon={Sparkles} title="Practice" color="#10B981">
              <div className="space-y-3">
                {lesson.practice.map((p, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`practice-${i}`}>
                    <p className="font-semibold text-slate-800 mb-2">{i + 1}. {p.q}</p>
                    <div className="flex items-center gap-2">
                      <Input value={pInputs[i] || ""} onChange={(e) => setPInputs({ ...pInputs, [i]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") checkPractice(i, p.a); }}
                        placeholder="Answer" className="h-10 max-w-[160px]" data-testid={`practice-input-${i}`} />
                      <Button size="sm" variant="outline" className="h-10 rounded-lg" onClick={() => checkPractice(i, p.a)} data-testid={`practice-check-${i}`}>Check</Button>
                      {checks[i] === "correct" && <span className="text-green-600 inline-flex items-center gap-1 text-sm font-semibold"><Check className="w-4 h-4" />Correct</span>}
                      {checks[i] === "wrong" && <span className="text-red-500 inline-flex items-center gap-1 text-sm font-semibold"><X className="w-4 h-4" />Ans: {p.a}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Button onClick={startMastery} className="w-full h-13 py-6 rounded-2xl text-lg font-bold bg-indigo-600 hover:bg-indigo-700" data-testid="start-mastery-btn">
              <Trophy className="w-5 h-5 mr-2" /> Take the Mastery Check
            </Button>
            <p className="text-center text-xs text-slate-400 mt-2">Score {quiz?.pass_mark || 80}%+ to master this lesson and unlock the next.</p>
          </>
        )}

        {phase === "mastery" && quiz && (
          <div className="space-y-3" data-testid="mastery-quiz">
            <p className="text-sm text-slate-500 mb-2">Answer all questions, then submit. No peeking! 🙂</p>
            {quiz.questions.map((q) => (
              <div key={q.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`mastery-q-${q.id}`}>
                <p className="font-semibold text-slate-800 mb-2">{q.q}</p>
                <Input value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  placeholder="Your answer" className="h-10 max-w-[180px]" data-testid={`mastery-input-${q.id}`} />
              </div>
            ))}
            <Button onClick={submitMastery} className="w-full h-12 rounded-2xl font-bold bg-indigo-600 hover:bg-indigo-700" data-testid="submit-mastery-btn">Submit Mastery Check</Button>
          </div>
        )}

        {phase === "result" && result && (
          <div className="text-center py-6" data-testid="mastery-result">
            <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-3 ${result.passed ? "bg-green-100" : "bg-amber-100"}`}>
              {result.passed ? <Trophy className="w-10 h-10 text-green-500" /> : <BookOpen className="w-10 h-10 text-amber-500" />}
            </div>
            <p className="text-5xl font-extrabold font-['Fredoka'] text-slate-800" data-testid="mastery-score">{result.score}%</p>
            <p className={`font-bold mt-1 ${result.passed ? "text-green-600" : "text-amber-600"}`}>
              {result.passed ? "Lesson mastered!" : `Keep practising — you need ${result.pass_mark}%`}
            </p>
            {result.passed && <p className="text-sm text-slate-500 mt-1">+{result.xp} XP earned</p>}
            {result.course_completed && <p className="mt-2 text-amber-700 bg-amber-100 rounded-full inline-block px-4 py-1 text-sm font-bold" data-testid="course-complete-badge">🎓 Course complete — certificate unlocked!</p>}
            <div className="flex gap-2 mt-6">
              {!result.passed && <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setPhase("learn")} data-testid="retry-btn">Review & retry</Button>}
              {result.passed && result.next_lesson && (
                <Button className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate(`/courses/${courseId}/${result.next_lesson}`)} data-testid="next-lesson-btn">Next lesson →</Button>
              )}
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => navigate("/courses")} data-testid="back-to-courses-btn">Back to Courses</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-slate-200 p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <h3 className="font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}
